import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { thunkGenerateCouponCodes, thunkBulkCreateCouponCodes, resetCouponState } from '../../redux/slices/couponSlice';
import { useAdminLayout } from "../../context/adminLayoutUseContext";

const CouponBulkGenerate = () => {
    const dispatch = useDispatch();
  const { loading_, error, codes, createdCount, message_ } = useSelector(state => state.coupon);

  const [formData, setFormData] = useState({
    count: 10,
    prefix: 'BULK',
    coupon_type: 'percentage',
    discount_value: '',
    start_date: '',
    end_date: '',
    minimum_order_amount: '0.00',
    maximum_order_amount: '',
    usage_type: 'unlimited',
    max_uses: 1,
    max_uses_per_user: 1,
    status: 'active'
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const { setLayoutConfig } = useAdminLayout();

  

  const couponTypes = [
    { value: 'percentage', label: 'Percentage Discount' },
    { value: 'fixed_amount', label: 'Fixed Amount Discount' },
    { value: 'free_shipping', label: 'Free Shipping' },
    { value: 'buy_x_get_y', label: 'Buy X Get Y Free' }
  ];

  const usageTypes = [
    { value: 'unlimited', label: 'Unlimited' },
    { value: 'limited', label: 'Limited Uses' },
    { value: 'single_use', label: 'Single Use' }
  ];

    useEffect(() => {
      setLayoutConfig({
        title: "Coupon / Bulk Generate",
        showBackButton: true,
        scrollable: true,
      });
     
    }, [dispatch,  setLayoutConfig]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
        dispatch(resetCouponState());

    try {
       const genResult = await dispatch(thunkGenerateCouponCodes({
        count: formData.count,
        prefix: formData.prefix,
        length: 6
      })).unwrap();


      const bulkData = {
        batch_name: `Bulk Generated - ${new Date().toLocaleDateString()}`,
        codes: genResult.codes,
        ...formData
      };

      const bulkResult = await dispatch(thunkBulkCreateCouponCodes(bulkData)).unwrap();

       setMessage(`Successfully created ${bulkResult.created_count} coupons!`);
      setMessageType('success');
      setGeneratedCodes(bulkResult.codes);



      setFormData({
        count: 10,
        prefix: 'BULK',
        coupon_type: 'percentage',
        discount_value: '',
        start_date: '',
        end_date: '',
        minimum_order_amount: '0.00',
        maximum_order_amount: '',
        usage_type: 'unlimited',
        max_uses: 1,
        max_uses_per_user: 1,
        status: 'active'
      });



    } catch (error) {
      setMessage('Error: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const downloadCodes = () => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      "Coupon Code,Type,Discount Value,Start Date,End Date\n" +
      generatedCodes.map(code => 
        `${code},${formData.coupon_type},${formData.discount_value},${formData.start_date},${formData.end_date}`
      ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `bulk_coupons_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bulk Generate Coupons</h1>
        <p className="text-gray-600 mt-2">Create multiple coupons with the same settings</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex">
            {messageType === 'success' ? (
              <CheckIcon className="h-5 w-5 mr-2 text-green-500" />
            ) : (
              <div className="h-5 w-5 mr-2 text-red-500">⚠</div>
            )}
            <span>{message}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Settings */}
          <div className="col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Settings</h3>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Coupons
            </label>
            <input
              type="number"
              name="count"
              value={formData.count}
              onChange={handleInputChange}
              min="1"
              max="1000"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code Prefix
            </label>
            <input
              type="text"
              name="prefix"
              value={formData.prefix}
              onChange={handleInputChange}
              maxLength="10"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coupon Type
            </label>
            <select
              name="coupon_type"
              value={formData.coupon_type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {couponTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Discount Value
            </label>
            <input
              type="number"
              name="discount_value"
              value={formData.discount_value}
              onChange={handleInputChange}
              step="0.01"
              min="0.01"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.coupon_type === 'percentage' ? 'Percentage (1-100)' : 'Fixed amount'}
            </p>
          </div>

          {/* Date Settings */}
          <div className="col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Validity Period</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="datetime-local"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="datetime-local"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Usage Settings */}
          <div className="col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Usage Settings</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usage Type
            </label>
            <select
              name="usage_type"
              value={formData.usage_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {usageTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {formData.usage_type === 'limited' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Uses
              </label>
              <input
                type="number"
                name="max_uses"
                value={formData.max_uses}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Uses Per User
            </label>
            <input
              type="number"
              name="max_uses_per_user"
              value={formData.max_uses_per_user}
              onChange={handleInputChange}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Order Requirements */}
          <div className="col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">Order Requirements</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Order Amount
            </label>
            <input
              type="number"
              name="minimum_order_amount"
              value={formData.minimum_order_amount}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Order Amount (Optional)
            </label>
            <input
              type="number"
              name="maximum_order_amount"
              value={formData.maximum_order_amount}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <PlusIcon className="h-4 w-4 mr-2" />
                Generate Coupons
              </>
            )}
          </button>
        </div>
      </form>

      {generatedCodes.length > 0 && (
        <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-green-800">
              Generated Codes ({generatedCodes.length})
            </h3>
            <button
              onClick={downloadCodes}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Download CSV
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-40 overflow-y-auto">
            {generatedCodes.map((code, index) => (
              <div
                key={index}
                className="bg-white px-3 py-2 rounded border text-center font-mono text-sm"
              >
                {code}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};



export default CouponBulkGenerate;